/**
 * Substrate validation (plan §4.2, test-plan §1a): family-independent checks on any
 * BoardAssembly. Families add their own named constraints on top.
 *
 * - no two parts overlap (AABB, 0.05 mm tolerance) unless a joint between them declares the
 *   interpenetration (a housed dado or a rebate is material removed from one of them);
 * - every part is supported: a horizontal touches at least two other parts that stand
 *   (verticals or the floor-standing parts under it), or one when it is a rated cantilever (a
 *   spine shelf deep-housed with a hidden `cantilever-bracket` in the build's hardware);
 *   anything else stands on the floor or touches another part;
 * - nothing lies outside the envelope;
 * - every joint names two existing, different parts that touch; a moving joint (hinge, runner, track: doors and
 *   drawers) may hold its part across a running gap of up to `MOVING_GAP_MM`, and counts as that part's support;
 * - every part fits its stock (or is a declared glue-up).
 */
import type { AssemblyPart, Hardware, Issue, Joint, StockFit } from './assembly';

export const TOL_MM = 0.05;

interface Box { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }
const box = (p: Pick<AssemblyPart, 'placement'>): Box => {
  const q = p.placement;
  return { x0: q.x, x1: q.x + q.sx, y0: q.y, y1: q.y + q.sy, z0: q.z, z1: q.z + q.sz };
};
/** Overlap length along one axis (negative = gap). */
const ov = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);

/** The two boxes share volume beyond the tolerance. */
export function overlaps(a: Box, b: Box, tol = TOL_MM): boolean {
  return ov(a.x0, a.x1, b.x0, b.x1) > tol && ov(a.y0, a.y1, b.y0, b.y1) > tol && ov(a.z0, a.z1, b.z0, b.z1) > tol;
}

/** The boxes touch or overlap: within the tolerance on every axis and sharing area on at least two. */
export function touches(a: Box, b: Box, tol = TOL_MM): boolean {
  const o = [ov(a.x0, a.x1, b.x0, b.x1), ov(a.y0, a.y1, b.y0, b.y1), ov(a.z0, a.z1, b.z0, b.z1)];
  if (o.some((v) => v < -tol)) return false;
  return o.filter((v) => v > tol).length >= 2;
}

/** A part lying flat: its thickness is its height. */
const isHorizontal = (p: AssemblyPart) => Math.abs(p.placement.sy - p.thicknessTicks / 10) < TOL_MM && p.placement.sx > p.placement.sy;

const INTERPENETRATING = new Set<Joint['kind']>(['housed-dado', 'rebate', 'egg-crate', 'mortise-tenon', 'through-tenon']);
const MOVING = new Set<Joint['kind']>(['hinge', 'runner', 'track']);
/** The widest running gap a moving joint spans: an inset door's 2.5 mm or a runner's 12.7 mm; a sliding panel runs in a track up to two panels proud. */
export const MOVING_GAP_MM: Readonly<Record<string, number>> = { hinge: 13, runner: 13, track: 120 };

/** Two boxes within `gap` of each other on every axis, sharing area on at least two (a moving joint's reach). */
function within(a: Box, b: Box, gap: number): boolean {
  const o = [ov(a.x0, a.x1, b.x0, b.x1), ov(a.y0, a.y1, b.y0, b.y1), ov(a.z0, a.z1, b.z0, b.z1)];
  return o.every((v) => v >= -gap) && o.filter((v) => v > TOL_MM).length >= 2;
}

export interface SubstrateInput {
  parts: readonly AssemblyPart[];
  joints: readonly Joint[];
  envelope: { W: number; H: number; D: number };
  stockFit: readonly StockFit[];
  /** The build's hardware: horizontals carrying a `cantilever-bracket` need one support, not two. */
  hardware?: readonly Hardware[];
}

export interface SubstrateResult {
  ok: boolean;
  errors: Issue[];
}

const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export function validateSubstrate(a: SubstrateInput): SubstrateResult {
  const errors: Issue[] = [];
  const parts = a.parts;
  const ids = new Set<string>();
  for (const p of parts) {
    if (ids.has(p.id)) errors.push({ field: p.id, message: `Two parts share the id ${p.id}.` });
    ids.add(p.id);
  }
  const boxes = new Map(parts.map((p) => [p.id, box(p)]));
  // a part on feet stands on the floor through them
  const onFeet = new Set((a.hardware ?? []).filter((h) => /foot$/.test(h.kind)).flatMap((h) => h.at.map((p) => p.partId)));
  const cantilevered = new Set((a.hardware ?? []).filter((h) => h.kind === 'cantilever-bracket').flatMap((h) => h.at.map((p) => p.partId)));
  const declared = new Set(a.joints.filter((j) => INTERPENETRATING.has(j.kind)).map((j) => pairKey(j.a, j.b)));

  // joints name two existing, different parts that touch
  for (const j of a.joints) {
    if (!ids.has(j.a) || !ids.has(j.b)) errors.push({ field: 'joints', message: `Joint ${j.id} names a part that does not exist (${!ids.has(j.a) ? j.a : j.b}).` });
    else if (j.a === j.b) errors.push({ field: 'joints', message: `Joint ${j.id} joins ${j.a} to itself.` });
    else if (MOVING.has(j.kind) ? !within(boxes.get(j.a)!, boxes.get(j.b)!, MOVING_GAP_MM[j.kind]!) : !touches(boxes.get(j.a)!, boxes.get(j.b)!)) errors.push({ field: 'joints', message: `Joint ${j.id}: ${j.a} and ${j.b} do not meet.` });
  }

  // overlaps
  for (let i = 0; i < parts.length; i++) {
    for (let k = i + 1; k < parts.length; k++) {
      const p = parts[i]!, q = parts[k]!;
      if (overlaps(boxes.get(p.id)!, boxes.get(q.id)!) && !declared.has(pairKey(p.id, q.id))) {
        errors.push({ field: 'substrate', message: `Parts overlap: ${p.id} and ${q.id}.` });
      }
    }
  }

  // envelope
  const E = a.envelope;
  for (const p of parts) {
    const b = boxes.get(p.id)!;
    if (b.x0 < -TOL_MM || b.y0 < -TOL_MM || b.z0 < -TOL_MM || b.x1 > E.W + TOL_MM || b.y1 > E.H + TOL_MM || b.z1 > E.D + TOL_MM) {
      errors.push({ field: 'substrate', message: `${p.label} lies outside the overall size.` });
    }
    if (![p.placement.sx, p.placement.sy, p.placement.sz].every((v) => Number.isFinite(v) && v > 0)) {
      errors.push({ field: 'substrate', message: `${p.label} has no size.` });
    }
  }

  // support
  const standing = (p: AssemblyPart) => !isHorizontal(p);
  const movingHeld = new Set(a.joints.filter((j) => MOVING.has(j.kind)).map((j) => j.a));
  for (const p of parts) {
    const b = boxes.get(p.id)!;
    const others = parts.filter((q) => q.id !== p.id && touches(b, boxes.get(q.id)!));
    if (isHorizontal(p)) {
      // rests on two standing parts (verticals), or on the floor plus one
      const supports = others.filter((q) => standing(q) && q.role !== 'back');
      const onFloor = b.y0 <= TOL_MM || onFeet.has(p.id);
      const need = cantilevered.has(p.id) ? 1 : 2;
      if (supports.length + (onFloor ? 1 : 0) < need) {
        errors.push({ field: 'substrate', message: need === 1 ? `${p.label} is not held by anything.` : `${p.label} needs two supports; it has ${supports.length + (onFloor ? 1 : 0)}.` });
      }
    } else if (b.y0 > TOL_MM && others.length === 0 && !movingHeld.has(p.id)) {
      errors.push({ field: 'substrate', message: `${p.label} is not attached to anything.` });
    }
  }

  // stock
  for (const f of a.stockFit) {
    if (!f.fits) errors.push({ field: f.partId, message: f.note });
  }
  return { ok: errors.length === 0, errors };
}
